import React, { useState, useEffect } from 'react';
import {
  createProfileListing,
  updateProfileListing,
  uploadImage,
  getZodiac,
  getStar,
  getCountries,
  getStates,
  getCities,
  getReligions,
  getCastes,
  getOccupations,
  getMotherTongue,
  getUserById,
  fetchMyProfiles
} from '../utils/api';
import { ClipLoader } from 'react-spinners';
import {
  mapUserToFormValues,
  shouldShowEntrepreneurFields,
  prepareSubmitData
} from '../utils/profileHelpers';

export function ProfileListingContent() {
  const [options, setOptions] = useState({
    zodiac: [], stars: [], countries: [], states: [], cities: [],
    religions: [], castes: [], occupations: [], motherTongues: [],
  });

  const [formValues, setFormValues] = useState({
    fullName: '', dob: '', gender: '', religion: '', occupation: '',
    annualIncome: '', caste: '', motherTongue: '', zodiacSign: '', star: '',
    maritalStatus: '', country: '', state: '', city: '', address: '', description: '',
    startupName: '', industry: '', linkedinProfile: '', companyName: '', expectedRevenue: '', founderVision: ''
  });

  const [filePreviews, setFilePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [images, setImages] = useState([]);
  const [profileVisibility, setProfileVisibility] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingProfile, setExistingProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [profileStatusMessage, setProfileStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const selectedOccupation = options.occupations.find(o => o._id === formValues.occupation);
  const showEntrepreneurFields = selectedOccupation?.title?.toLowerCase().includes('entrepreneur');

  // Load dropdown options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [
          zodiac, stars, countries, states, cities,
          religions, castes, occupations, motherTongues
        ] = await Promise.all([
          getZodiac(), getStar(), getCountries(),
          getStates(), getCities(), getReligions(),
          getCastes(), getOccupations(), getMotherTongue()
        ]);

        setOptions({
          zodiac, stars, countries, states,
          cities, religions, castes, occupations,
          motherTongues
        });
      } catch (err) {
        console.error('Failed to load options', err);
      }
    };

    loadOptions();
  }, []);

  // Load user data and check profile status
  useEffect(() => {
    const loadUserData = async () => {
      const userId = localStorage.getItem('matrimonyUserId');
      if (!userId) {
        setIsLoading(false);
        return;
      }

      try {
        // Check profile status first
        const profileResponse = await fetchMyProfiles();
        if (profileResponse.success && profileResponse.data) {
          const userProfile = profileResponse.data.find(profile => profile.user === userId);
          if (userProfile) {
            setExistingProfile(userProfile);
            setProfileStatusMessage('Your profile has already been listed');
            setIsLoading(false);
            return;
          }
        }

        // If no profile exists, load user data for form
        setProfileStatusMessage('Your profile is not yet listed');
        const user = await getUserById(userId);
        const formData = mapUserToFormValues(user, options);
        setFormValues(formData);

        if (user.images) {
          setImages(user.images);
          setFilePreviews(user.images);
        }
        setProfileVisibility(user.visibility === 'Public');
      } catch (err) {
        console.error('Error loading data:', err);
        setProfileStatusMessage('Error loading profile data');
      } finally {
        setIsLoading(false);
      }
    };

    if (options.occupations.length > 0) { // Only load user data after options are loaded
      loadUserData();
    }
  }, [options]);

  const handleChange = e => {
    const { id, value } = e.target;
    setFormValues(prev => ({ ...prev, [id]: value }));
  };

  const handleImageUpload = async e => {
    const files = Array.from(e.target.files);
    setFilePreviews(files.map(f => URL.createObjectURL(f)));
    const uploaded = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const res = await uploadImage(files[i], pct =>
          setUploadProgress(p => ({ ...p, [i]: pct }))
        );
        uploaded.push(res.url);
      } catch {
        setUploadProgress(p => ({ ...p, [i]: 0 }));
      }
    }
    setImages(uploaded);
  };

  const removeImage = idx => {
    setFilePreviews(prev => prev.filter((_, i) => i !== idx));
    setImages(prev => prev.filter((_, i) => i !== idx));
    setUploadProgress(prev => {
      const copy = { ...prev };
      delete copy[idx];
      return copy;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = prepareSubmitData(
        formValues,
        images,
        profileVisibility,
        options.occupations
      );

      if (isEditing && existingProfile) {
        // Update existing profile
        const updatedProfile = await updateProfileListing(existingProfile._id, payload);
        setExistingProfile(updatedProfile);
        setProfileStatusMessage('Profile updated successfully!');
        setIsEditing(false);
      } else {
        // Create new profile
        const newProfile = await createProfileListing(payload);
        setExistingProfile(newProfile);
        setProfileStatusMessage('Profile created successfully!');
      }
    } catch (err) {
      console.error('Submission failed:', err);
      setProfileStatusMessage(`Failed to ${isEditing ? 'update' : 'create'} profile. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditProfile = () => {
    if (existingProfile) {
      // Map the existing profile data to form values
      const formData = {
        fullName: existingProfile.fullName || '',
        dob: existingProfile.dob ? new Date(existingProfile.dob).toISOString().split('T')[0] : '',
        gender: existingProfile.gender || '',
        religion: existingProfile.religion?._id || existingProfile.religion || '',
        occupation: existingProfile.occupation?._id || existingProfile.occupation || '',
        annualIncome: existingProfile.annualIncome || '',
        caste: existingProfile.caste?._id || existingProfile.caste || '',
        motherTongue: existingProfile.motherTongue?._id || existingProfile.motherTongue || '',
        zodiacSign: existingProfile.zodiacSign?._id || existingProfile.zodiacSign || '',
        star: existingProfile.star?._id || existingProfile.star || '',
        maritalStatus: existingProfile.maritalStatus || '',
        country: existingProfile.country?._id || existingProfile.country || '',
        state: existingProfile.state?._id || existingProfile.state || '',
        city: existingProfile.city?._id || existingProfile.city || '',
        address: existingProfile.address || '',
        description: existingProfile.description || '',
        startupName: existingProfile.startupName || '',
        industry: existingProfile.industry || '',
        linkedinProfile: existingProfile.linkedinProfile || '',
        companyName: existingProfile.companyName || '',
        expectedRevenue: existingProfile.expectedRevenue || '',
        founderVision: existingProfile.founderVision || ''
      };

      setFormValues(formData);

      if (existingProfile.images) {
        setImages(existingProfile.images);
        setFilePreviews(existingProfile.images.map(img => typeof img === 'string' ? img : img.url));
      }
      setProfileVisibility(existingProfile.visibility === 'Public');
      setIsEditing(true);
      setProfileStatusMessage('Editing your profile...');
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 bg-gray-50 flex items-center justify-center">
        <ClipLoader size={50} color="#E7603C" />
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-50">
      <div className="p-8">
        <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-2xl md:text-3xl font-semibold text-amber-700 text-center mb-8">
            Let's showcase the groom's or bride's details
          </h1>

          <div className={`mb-6 p-4 rounded-md text-center ${existingProfile ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
            {profileStatusMessage}
          </div>

          {existingProfile && !isEditing ? (
            <div className="text-center py-8 space-y-4">
              <p className="text-lg text-gray-700">
                Your profile is already listed in our system.
              </p>
              <button
                onClick={handleEditProfile}
                className="px-6 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors duration-200"
              >
                Edit Profile
              </button>
            </div>
          ) : (

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <label htmlFor="fullName" className="text-sm font-medium text-gray-700">Full Name</label>
                  <input id="fullName" type="text" value={formValues.fullName} onChange={handleChange} placeholder="Enter Name" className="w-full border rounded px-3 py-2" required />
                </div>

                {/* Date of Birth */}
                <div className="space-y-2">
                  <label htmlFor="dob" className="text-sm font-medium text-gray-700">Date of Birth</label>
                  <input id="dob" type="date" value={formValues.dob} onChange={handleChange} className="w-full border rounded px-3 py-2" required />
                </div>

                {/* Gender */}
                <div className="space-y-2">
                  <label htmlFor="gender" className="text-sm font-medium text-gray-700">Gender</label>
                  <select id="gender" value={formValues.gender} onChange={handleChange} className="w-full border rounded px-3 py-2" required>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Religion */}
                <div className="space-y-2">
                  <label htmlFor="religion" className="text-sm font-medium text-gray-700">Religion</label>
                  <select id="religion" value={formValues.religion} onChange={handleChange} className="w-full border rounded px-3 py-2">
                    <option value="">Choose Religion</option>
                    {options.religions.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
                  </select>
                </div>

                {/* Caste */}
                <div className="space-y-2">
                  <label htmlFor="caste" className="text-sm font-medium text-gray-700">Caste</label>
                  <select id="caste" value={formValues.caste} onChange={handleChange} className="w-full border rounded px-3 py-2">
                    <option value="">Choose Caste</option>
                    {options.castes.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>

                {/* Occupation */}
                <div className="space-y-2">
                  <label htmlFor="occupation" className="text-sm font-medium text-gray-700">Occupation</label>
                  <select
                    id="occupation"
                    value={formValues.occupation}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="">Choose Occupation</option>
                    {options.occupations.map((o) => (
                      <option key={o._id} value={o._id}>{o.title}</option>
                    ))}
                  </select>
                </div>

                {/* Entrepreneur Fields - Conditionally Rendered */}
                {showEntrepreneurFields && (
                  <>
                    <div className="space-y-2 col-span-full">
                      <h3 className="text-lg font-medium text-gray-700">Entrepreneur Details</h3>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="companyName" className="text-sm font-medium text-gray-700">Company Name</label>
                      <input
                        id="companyName"
                        type="text"
                        value={formValues.companyName}
                        onChange={handleChange}
                        placeholder="Enter Company Name"
                        className="w-full border rounded px-3 py-2"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="expectedRevenue" className="text-sm font-medium text-gray-700">Expected Revenue</label>
                      <input
                        id="expectedRevenue"
                        type="text"
                        value={formValues.expectedRevenue}
                        onChange={handleChange}
                        placeholder="Enter Expected Revenue"
                        className="w-full border rounded px-3 py-2"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="founderVision" className="text-sm font-medium text-gray-700">Founder's Vision</label>
                      <textarea
                        id="founderVision"
                        value={formValues.founderVision}
                        onChange={handleChange}
                        placeholder="Describe your vision for the company"
                        className="w-full border rounded px-3 py-2"
                        rows={3}
                        required
                      />
                    </div>
                  </>
                )}

                {/* Annual Income */}
                <div className="space-y-2">
                  <label htmlFor="annualIncome" className="text-sm font-medium text-gray-700">Annual Income</label>
                  <input id="annualIncome" type="text" value={formValues.annualIncome} onChange={handleChange} placeholder="Enter Annual Income" className="w-full border rounded px-3 py-2" />
                </div>

                {/* Mother Tongue */}
                <div className="space-y-2">
                  <label htmlFor="motherTongue" className="text-sm font-medium text-gray-700">Mother Tongue</label>
                  <select id="motherTongue" value={formValues.motherTongue} onChange={handleChange} className="w-full border rounded px-3 py-2">
                    <option value="">Choose Mother Tongue</option>
                    {options.motherTongues.map((mt) => <option key={mt._id} value={mt._id}>{mt.name}</option>)}
                  </select>
                </div>

                {/* Zodiac Sign */}
                <div className="space-y-2">
                  <label htmlFor="zodiacSign" className="text-sm font-medium text-gray-700">Zodiac Sign</label>
                  <select id="zodiacSign" value={formValues.zodiacSign} onChange={handleChange} className="w-full border rounded px-3 py-2">
                    <option value="">Choose Zodiac Sign</option>
                    {options.zodiac.map((z) => <option key={z._id} value={z._id}>{z.name}</option>)}
                  </select>
                </div>

                {/* Star */}
                <div className="space-y-2">
                  <label htmlFor="star" className="text-sm font-medium text-gray-700">Star</label>
                  <select id="star" value={formValues.star} onChange={handleChange} className="w-full border rounded px-3 py-2">
                    <option value="">Choose Star</option>
                    {options.stars.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                </div>

                {/* Marital Status */}
                <div className="space-y-2">
                  <label htmlFor="maritalStatus" className="text-sm font-medium text-gray-700">Marital Status</label>
                  <select id="maritalStatus" value={formValues.maritalStatus} onChange={handleChange} className="w-full border rounded px-3 py-2">
                    <option value="">Choose Marital Status</option>
                    <option value="Single">Single</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                    <option value="Separated">Separated</option>
                  </select>
                </div>

                {/* Country */}
                <div className="space-y-2">
                  <label htmlFor="country" className="text-sm font-medium text-gray-700">Country</label>
                  <select id="country" value={formValues.country} onChange={handleChange} className="w-full border rounded px-3 py-2">
                    <option value="">Choose Country</option>
                    {options.countries.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>

                {/* State */}
                <div className="space-y-2">
                  <label htmlFor="state" className="text-sm font-medium text-gray-700">State</label>
                  <select id="state" value={formValues.state} onChange={handleChange} className="w-full border rounded px-3 py-2">
                    <option value="">Choose State</option>
                    {options.states.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                </div>

                {/* City */}
                <div className="space-y-2">
                  <label htmlFor="city" className="text-sm font-medium text-gray-700">City</label>
                  <select id="city" value={formValues.city} onChange={handleChange} className="w-full border rounded px-3 py-2">
                    <option value="">Choose City</option>
                    {options.cities.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>

                {/* Address */}
                <div className="space-y-2 col-span-full">
                  <label htmlFor="address" className="text-sm font-medium text-gray-700">Address</label>
                  <input id="address" type="text" value={formValues.address} onChange={handleChange} placeholder="Enter address" className="w-full border rounded px-3 py-2" />
                </div>

                {/* Upload Images */}
                <div className="col-span-full">
                  <label className="text-sm font-medium text-gray-700">Upload Images</label>
                  <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="w-full border rounded px-3 py-2 mt-1" />
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    {filePreviews.map((url, idx) => (
                      <div key={idx} className="relative">
                        <img src={url} alt={`preview-${idx}`} className="w-full h-32 object-cover rounded" />
                        {uploadProgress[idx] != null && uploadProgress[idx] < 100 && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="relative w-12 h-12">
                              <svg viewBox="0 0 36 36" className="w-full h-full">
                                <path className="text-gray-300" fill="none" strokeWidth="3" d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831" />
                                <path className="text-[#E7603C]" fill="none" strokeWidth="3" strokeDasharray={`${uploadProgress[idx]},100`} d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831" />
                              </svg>
                              <span className="absolute inset-0 flex items-center justify-center text-sm text-white">{uploadProgress[idx]}%</span>
                            </div>
                          </div>
                        )}
                        <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white bg-opacity-75 px-1 rounded-full hover:bg-opacity-100">×</button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2 col-span-full">
                  <label htmlFor="description" className="text-sm font-medium text-gray-700">Description</label>
                  <textarea id="description" value={formValues.description} onChange={handleChange} placeholder="Enter Description" className="w-full border rounded px-3 py-2" rows={3} />
                </div>

                {/* Visibility */}
                <div className="flex items-center space-x-3">
                  <input type="checkbox" checked={profileVisibility} onChange={() => setProfileVisibility((v) => !v)} className="w-5 h-5" />
                  <span className="text-sm font-medium text-gray-700">{profileVisibility ? 'Public' : 'Private'}</span>
                </div>

                {/* Submit Button */}
                <div className="col-span-full text-right">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    {isSubmitting && <ClipLoader size={20} color="#ffffff" />}
                    <span>{isSubmitting ? 'Processing...' : isEditing ? 'Update Profile' : 'Create Profile'}</span>
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}